import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Lock, Trash2 } from 'lucide-react';
import type { TicketComment } from '@/services/tech-support.service';
import { techSupportService } from '@/services/tech-support.service';
import { usePermission } from '@/hooks/usePermission';
import { useAuth } from '@/hooks/useAuth';
import { PERMISSIONS } from '@pms/shared';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';

interface Props {
  ticketId: string;
  comments: TicketComment[];
}

export function TicketCommentSection({ ticketId, comments }: Props) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { has } = usePermission();
  const canViewAll = has(PERMISSIONS.TECH_SUPPORT_VIEW_ALL);

  const [text, setText] = useState('');
  const [isInternal, setIsInternal] = useState(false);

  const addMutation = useMutation({
    mutationFn: () =>
      techSupportService.addComment(ticketId, { comment_text: text.trim(), is_internal: isInternal }),
    onSuccess: () => {
      toast.success('Comment added');
      setText('');
      setIsInternal(false);
      qc.invalidateQueries({ queryKey: ['tech-support', 'tickets', ticketId] });
    },
    onError: () => toast.error('Failed to add comment'),
  });

  const deleteMutation = useMutation({
    mutationFn: (commentId: string) => techSupportService.deleteComment(ticketId, commentId),
    onSuccess: () => {
      toast.success('Comment deleted');
      qc.invalidateQueries({ queryKey: ['tech-support', 'tickets', ticketId] });
    },
    onError: () => toast.error('Failed to delete comment'),
  });

  const visibleComments = canViewAll ? comments : comments.filter((c) => !c.is_internal);

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold text-foreground">
        Comments ({visibleComments.length})
      </h3>

      {visibleComments.length === 0 ? (
        <p className="text-sm text-muted-foreground italic">No comments yet.</p>
      ) : (
        <div className="divide-y divide-border/60">
          {visibleComments.map((c) => (
            <div key={c.id} className={`py-3 ${c.is_internal ? 'bg-amber-50/40 -mx-1 px-1 rounded' : ''}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="text-xs font-semibold text-foreground">{c.commented_by.name}</span>
                    {c.is_internal && (
                      <Badge variant="warning">
                        <Lock className="w-2.5 h-2.5 mr-0.5" />
                        Internal
                      </Badge>
                    )}
                    <span className="text-xs text-muted-foreground">
                      {format(new Date(c.created_at), 'MMM d, h:mm a')}
                    </span>
                  </div>
                  <p className="text-sm text-foreground whitespace-pre-wrap">{c.comment_text}</p>
                </div>
                {c.commented_by.id === user?.id && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 shrink-0 text-muted-foreground hover:text-destructive"
                    onClick={() => deleteMutation.mutate(c.id)}
                    disabled={deleteMutation.isPending}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="space-y-3 pt-2">
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={3}
          placeholder="Write a comment…"
        />
        <div className="flex items-center justify-between gap-3">
          {canViewAll && (
            <div className="flex items-center gap-2">
              <Checkbox
                id="internal"
                checked={isInternal}
                onChange={(e) => setIsInternal(e.target.checked)}
              />
              <Label htmlFor="internal" className="flex items-center gap-1 cursor-pointer text-muted-foreground">
                <Lock className="w-3.5 h-3.5 text-amber-500" />
                Internal note
              </Label>
            </div>
          )}
          <Button
            className="ml-auto"
            onClick={() => { if (text.trim()) addMutation.mutate(); }}
            disabled={!text.trim() || addMutation.isPending}
          >
            {addMutation.isPending ? 'Posting…' : 'Post Comment'}
          </Button>
        </div>
      </div>
    </div>
  );
}
